import { defineComponent as _defineComponent } from 'vue'

// Packages
import { defineComponent, PropType } from 'vue';
import VueI18n from 'vue-i18n';

// Types
import type {
  CartItem,
  Total,
  Receipt,
  Bundle,
} from '@white-label-types/parking-checkout';
import type { TaxFee, BookingFee } from '@white-label-types/parking-booking';

// Helpers
import { formatPrice } from '@white-label-helper/helper-payment';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { BOOKING_STATUSES, PRODUCT_TYPES, NAMED_ROUTES, FEATURE_FLAGS } from '@white-label-configuration/constants';

// Components
import ProductSummaryCard from '../product-summary-card/product-summary-card.vue';
import ProductSummaryBundleCard from '../product-summary-card/product-summary-bundle-card.vue';
import SummaryAirport from '../summary-airport/summary-airport.vue';

// Stores
import useDepositStore from '@white-label-store/deposits'

type SummaryElementItems = CartItem[] | Receipt['items'];
type SubItems = {
  title: string;
  value: string | Intl.NumberFormat;
  isDiscount?: boolean;
};

type InfoMessage = { text: string; slot0?: string };

const __default__ = defineComponent({
  name: 'PaymentSummaryDropdown',

  components: {
    IconArrow: () => import('@white-label-icon/icon-arrow'),
    ProductSummaryCard,
    ProductSummaryBundleCard,
    SummaryAirport
  },

  props: {
    amountToPay: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    items: {
      type: Array as PropType<SummaryElementItems>,
      default: () => [] as SummaryElementItems,
    },
    bundle: {
      type: Object as PropType<Bundle | null>,
      default: null,
    },
    originalOrder: {
      type: Object as PropType<SummaryElementItems[0]>,
      required: false,
      default: () => ({}),
    },
    hasPaid: {
      type: Boolean,
      default: false,
    },
    isModifyFlowReceipt: {
      type: Boolean,
      default: false,
    },
    isRefund: {
      type: Boolean,
      default: false,
    },
    isPartialRefund: {
      type: Boolean,
      default: false,
    },
    orderTotals: {
      type: Object as PropType<Total>,
      default: () => ({}),
    },
    priceDifference: {
      type: String,
      default: '',
    },
    isCancelling: {
      type: Boolean,
      default: false,
    },
    refundDoesNotExist: {
      type: Boolean,
      default: false,
    },
    refundProcessing: {
      type: Boolean,
      default: false,
    },
    valueAfterRefundWithoutCancellation: {
      type: String,
      default: '',
    },
    bookingFee: {
      type: Object as PropType<BookingFee>,
      default: () => ({}),
    },
    singleCancelItemId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      dropdownIsActive: false,
      showDiscountBreakdown: false,
      showTaxesBreakdown: false,
      isExtrasRoute:false,
    };
  },
  computed: {

    iconArrowClassesTwo() {
      return [
        'text-mag-elements-icons-on-dark hide-on-print',
        { 'transform rotate-180': this.showDiscountBreakdown }
      ]
    },

    iconArrowClassesThree() {
      return [
        'text-mag-elements-icons-on-dark hide-on-print',
        { 'transform rotate-180': this.showTaxesBreakdown }
      ]
    },

    sortedItems(): SummaryElementItems {
      return this.items.slice().sort((a, b) => {
        const dateA = new Date(a.details?.entry_date).getTime();
        const dateB = new Date(b.details?.entry_date).getTime();

        if (dateA === dateB) {
          const nameA = (a.inventory_item.display_name || a.inventory_item.name)?.toLowerCase();
          const nameB = (b.inventory_item.display_name || b.inventory_item.name)?.toLowerCase();
          return nameA.localeCompare(nameB);
        }

        return dateA - dateB;
      }).filter((item) => !item.bundle_id);
    },

    discountItems(): { name: string, price: string } {
      const items = this.sortedItems
        .filter((item) => item.totals.discount !== 0)
        .map((item) => ({
          name: item.inventory_item.display_name || item.inventory_item.name,
          price: `-${formatPrice(item.totals.discount)}`,
        }));

      if (this.bundle && this.bundle.totals?.discount) {
        items.push({
          name: this.bundle.name,
          price: `-${formatPrice(this.bundle.totals.discount)}`,
        })
      }
      return items;
    },

    isModifyCheckout(): boolean {
      return !!this.originalItemTotalPrice;
    },

    originalOrderProps(): Record<string, any> {
      const item = this.originalOrder;
      if (!item.inventory_option) {
        return {};
      }
      const sub_options = item.inventory_option.sub_options || [];
      const labels: Record<string, string> = {
        senior: 'Senior',
        adult: 'Adult',
        child: 'Child',
        infant: 'Infant',
      };
      const subItems: SubItems[] = [];
      const inventoryMap: Record<string, any> = {};
      sub_options.forEach((listItem) => {
        if (listItem?.details?.name) {
          inventoryMap[listItem.details.name] = listItem;
        }
      });
      ['senior', 'adult', 'child', 'infant'].forEach((key) => {
        const item = inventoryMap[key];
        if (!item) {
          return;
        }
        // Added label[key] until cart data response has added name_formatted
        const name = item.details?.name_formatted
          ? item.details.name
          : labels[key];
        const totals =
          'original_amounts' in item
            ? item.original_amounts.totals
            : item.totals;
        const quantity = item.quantity || totals.quantity;
        if (quantity) {
          const label = `${quantity} x ${name}`;
          subItems.push({
            title: label,
            value: formatPrice(totals.subtotal),
          });
          if (item.discount) {
            subItems.push({
              isDiscount: true,
              title: item.discount.description,
              value: formatPrice(item.discount.amount),
            });
          }
        }
      });

      const displayName =
        item?.inventory_item && 'display_name' in item.inventory_item
          ? item.inventory_item.display_name
          : '';

      const orderName = displayName || item?.inventory_item?.name || '';

      return {
        taxes: item.taxes,
        fees: item.fees,
        orderName,
        subItems,
      };
    },

    cancellationProtection(): Record<string, any> {
      const cancellationProtectionProduct = {
        is_protected: false,
        product_code: 'cancellation_protection',
        inventory_item: {
          name: this.$t('shared.summaryLounges.cancellationProtection'),
        },
        totals: {
          total: 0
        },
      };

      this.items.forEach((item) => {
        if (item.is_protected || item.cancellation_policies?.is_protected) {
          const cancellationProtectionPrice = item.cancellation_protection?.totals?.total ?? 0;

          cancellationProtectionProduct.is_protected = true;
          cancellationProtectionProduct.totals.total = cancellationProtectionProduct.totals.total + cancellationProtectionPrice;
        }
      });

      return cancellationProtectionProduct;
    },

    isAmending(): boolean {
      const item = this.items[0];
      return item && 'meta' in item && !!item.meta?.original_order_item_id;
    },
    originalItemTotalPrice(): string {
      const item = this.items[0];
      if (item && 'meta' in item) {
        return `${item?.meta?.original_order_item_total_price || ''}`;
      }
      return '';
    },

    /**
     * Text to be display when in modify/amendment flow for either refunds or payment due
     */
    modifyFlow(): VueI18n.TranslateResult {
      const useDepositOnly = useDepositStore();

      if (useDepositOnly.shouldShowDepositInformation) {
        if (this.isRefund) {
          // We own the customer
          return this.$t('shared.amountRefund');
        } else {
          // Customer owns us
          return this.$t('shared.payNow');
        }
      }

      if (this.isRefund) {
        return this.$t('shared.refundAmount');
      }

      if (this.amountToPay === 0) {
        return this.$t('shared.total');
      }

      return this.$t('shared.total');
    },

    /**
     * Text to be display when in the normal checkout flow
     */
    normalFlow(): VueI18n.TranslateResult {
      const useDepositOnly = useDepositStore();
      if (useDepositOnly.shouldShowDepositInformation) {
        return this.$t('shared.payNow');
      }

      return this.$t('shared.total');
    },

    issuingRefund(): VueI18n.TranslateResult {
      if (this.amountToPay === 0) {
        return this.$t('shared.total');
      }

      const useDepositOnly = useDepositStore();
      if (useDepositOnly.shouldShowDepositInformation) {
        return this.$t('shared.amountRefund');
      }

      return this.$t('shared.refundAmount');
    },

    totalLabel(): VueI18n.TranslateResult {
      if (this.isModifyCheckout) {
        return this.modifyFlow;
      }

      if (this.isModifyFlowReceipt) {
        if (this.amountToPay !== null && this.amountToPay < 0) {
          return this.$t('shared.refundAmount');
        }
        if (this.amountToPay !== null && this.amountToPay > 0) {
          return this.$t('shared.amountPaid');
        }
        return this.$t('shared.priceDifference');
      }

      if (this.amountToPay !== null && this.amountToPay > 0) {
        return this.$t('shared.total');
      }

      if (this.isRefund) {
        return this.issuingRefund;
      }

      if (this.hasPaid) {
        return this.$t('shared.totalPaid');
      }

      return this.normalFlow;
    },
    orderTotalFormatted(): string | Intl.NumberFormat {
      return typeof this.orderTotals?.total === 'number'
        ? formatPrice(this.orderTotals.total)
        : '';
    },
    totalAfterRefundsFormatted(): ReturnType<typeof formatPrice> {
      let totalPaid = (this.orderTotals.total - this.priceDifference) - this.orderTotals.cancellation_fee_total;
      return formatPrice(totalPaid)
    },

    totalValue(): string | Intl.NumberFormat {
      const useDepositOnly = useDepositStore();

      if (
        useDepositOnly.cartTotalDepositAmount
        && !(this.isModifyCheckout || this.isCancelling)
      ) {

        // If were on the ecom receipt page after doing an amendment, show the price without the amendment fee
        if (
          (this.$route.name === 'receipt' && this.$route.query['modify-receipt'])
          && this.orderTotals.paid_without_amendment_fee_formatted
        ) {
          return this.orderTotals.paid_without_amendment_fee_formatted.with_currency_sign;
        }

        return this.isRefund || this.isModifyCheckout
          ? this.priceDifference
          :  useDepositOnly.cartTotalDepositAmount.payNow;
      }

      if (!this.isModifyFlowReceipt) {
        if (this.valueAfterRefundWithoutCancellation) {
          return this.valueAfterRefundWithoutCancellation;
        }

        if (this.isPartialRefund) {
          return this.totalAfterRefundsFormatted;
        }

        return this.isRefund || this.isModifyCheckout
          ? this.priceDifference
          : this.orderTotalFormatted;
      }

      return this.priceDifference;
    },
    // NOSONAR
    infoMessage(): InfoMessage | string {
      // NOSONAR
      const titleKey = this.originalOrder.product_code === 'parking'
        // NOSONAR
        ? 'shared.summaryParkings'
        // NOSONAR
        : 'shared.summaryLounges';
      // NOSONAR
      if (this.amountToPay !== null && this.amountToPay > 0) {
        // NOSONAR
        return {
          // NOSONAR
          text: titleKey + '.originalPriceIsLower',
          // NOSONAR
          slot0: titleKey + '.originalPriceIsLowerSlot0',
          // NOSONAR
        };
      }
      if (this.amountToPay !== null && this.amountToPay < 0) {
        return {
          text: titleKey + '.originalPriceIsHigher',
          slot0: titleKey + '.originalPriceIsHigherSlot0',
        };
      }
      if (this.amountToPay === 0) {
        return {
          text: titleKey + '.sameAsOriginalPrice',
          slot0: titleKey + '.sameAsOriginalPriceSlot0',
        };
      }
      if (this.refundDoesNotExist) {
        return {
          text: titleKey + '.refundDoesNotExist',
        };
      }
      if (this.refundProcessing) {
        return {
          text: titleKey + '.refundProcessing',
        };
      }
      return '';
    },

    displayDiscountSection(): boolean {
      return this.orderTotals.discount !== 0
        && this.discountItems.length !== 0
        && this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIES);
    },

    combinedTaxes(): TaxFee[] {
      // Create a map to store unique items by their name
      const itemMap = new Map();

      this.items.forEach((item) => {
        item.taxes.forEach((tax) => {
          // If the item name is already in the map, sum the amounts
          if (itemMap.has(tax.name)) {
            itemMap.get(tax.name).amount += tax.amount;
          } else {
            // Otherwise, add the item to the map
            itemMap.set(tax.name, { ...tax });
          }
        })
      });

      // Convert the map values back to an array
      return Array.from(itemMap.values());
    },

    taxesTotal(): number {
      return this.items.reduce((previousItem, currentItem) => previousItem + currentItem.totals.taxes ,0);
    },

    displayTaxesSection(): boolean {
      return this.combinedTaxes.length !== 0
        && !getAppVariable('is_tax_inclusive')
        && this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIESNT_SUMMARIESNT_SUMMARIESNT_SUMMARIES);
    },

    bookingFeeAmountFormatted(): string | Intl.NumberFormat {
      return formatPrice(this.bookingFee.amount);
    },
  },
  mounted() {
    const { path } = this.$route || {};
    this.isExtrasRoute = path?.includes(NAMED_ROUTES.select_extras.product) ?? false;
  },
  methods: {
    toggleDropdown() {
      this.dropdownIsActive = !this.dropdownIsActive;
    },
    isExtrasProduct(product){
      return product.product_code === PRODUCT_TYPES.EXTRAS;
    },
    formatPrice,
    isProductSelectedForCancellation(product: any): boolean {
      return (
        (product.id.toString() === this.singleCancelItemId) ||
        (this.isCancelling && this.singleCancelItemId === '' && product?.status?.toUpperCase() !== BOOKING_STATUSES.CANCELLED)
      );
    },
  },
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {


return { __sfc: true,formatPrice, ProductSummaryCard, ProductSummaryBundleCard, SummaryAirport }
}

})